import * as React from 'react';
import { cn } from '@/shared/lib/cn';

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('rounded-[10px] border-0 bg-surface-high text-foreground shadow-sm', className)}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    // Content arrives as props.children through the spread, which the rule cannot see.
    // eslint-disable-next-line jsx-a11y/heading-has-content
    <h3
      ref={ref}
      className={cn('text-lg font-semibold leading-none tracking-tight', className)}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

interface CardActionHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  action?: React.ReactNode;
}

/** Title with a left accent bar + an optional right-aligned action (e.g. a Save button). */
const CardActionHeader = React.forwardRef<HTMLDivElement, CardActionHeaderProps>(
  ({ className, title, action, ...props }, ref) => (
    <CardHeader
      ref={ref}
      className={cn('flex-row items-center justify-between gap-4 space-y-0', className)}
      {...props}
    >
      <div className="flex items-center gap-2.5">
        <span className="h-4 w-0.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
        <CardTitle className="text-base">{title}</CardTitle>
      </div>
      {action}
    </CardHeader>
  ),
);
CardActionHeader.displayName = 'CardActionHeader';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  ),
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardActionHeader, CardTitle, CardContent, CardFooter };
